import { useState, useRef, useCallback } from 'react'
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react'
import { cn } from '@shared/lib/utils'
import { Button } from '@shared/components/ui/button'

interface ImageUploadProps {
  value?: string
  onChange: (url: string) => void
  onRemove: () => void
  label?: string
  description?: string
  maxSizeMB?: number
  aspectRatio?: string
  className?: string
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']

export function ImageUpload({
  value,
  onChange,
  onRemove,
  label = 'Upload image',
  description = 'PNG, JPG, GIF or WebP (max. 5MB)',
  maxSizeMB = 5,
  className
}: ImageUploadProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const maxFileSize = maxSizeMB * 1024 * 1024

  const validateFile = useCallback((file: File): string | null => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return 'Please upload a valid image file (PNG, JPG, GIF, or WebP)'
    }
    if (file.size > maxFileSize) {
      return `File size must be less than ${maxSizeMB}MB`
    }
    return null
  }, [maxFileSize, maxSizeMB])

  const handleFile = useCallback(async (file: File) => {
    setError(null)
    const validationError = validateFile(file)
    if (validationError) {
      setError(validationError)
      return
    }
    try {
      setIsUploading(true)
      const reader = new FileReader()
      reader.onloadend = () => {
        onChange(reader.result as string)
        setIsUploading(false)
      }
      reader.onerror = () => {
        setError('Failed to read file')
        setIsUploading(false)
      }
      reader.readAsDataURL(file)
    } catch {
      setError('Failed to upload image')
      setIsUploading(false)
    }
  }, [onChange, validateFile])

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(true)
  }, [])
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false)
  }, [])
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation()
  }, [])
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false)
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) handleFile(files[0])
  }, [handleFile])
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) handleFile(files[0])
  }, [handleFile])
  const handleBrowseClick = () => fileInputRef.current?.click()
  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation()
    onRemove()
    setError(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // Uploaded state — compact thumbnail row
  if (value && !isUploading) {
    return (
      <div className={cn('w-full', className)}>
        {label && <div className="text-sm font-medium text-foreground mb-1.5">{label}</div>}
        <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-border bg-neutral-50">
          <div className="h-9 w-14 rounded-md overflow-hidden shrink-0 border border-border bg-white">
            <img src={value} alt="Uploaded preview" className="w-full h-full object-cover" />
          </div>
          <p className="flex-1 text-sm text-muted-foreground truncate">Image uploaded</p>
          <div className="flex items-center gap-1 shrink-0">
            <Button type="button" variant="ghost" size="sm" onClick={handleBrowseClick} className="h-7 text-xs px-2">
              Replace
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={handleRemove} className="h-7 text-xs px-2 text-destructive hover:text-destructive hover:bg-destructive/10">
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
        <input ref={fileInputRef} type="file" accept={ACCEPTED_TYPES.join(',')} onChange={handleFileInputChange} className="hidden" />
      </div>
    )
  }

  // Empty / uploading state — compact strip
  return (
    <div className={cn('w-full', className)}>
      {label && <div className="text-sm font-medium text-foreground mb-1.5">{label}</div>}
      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleBrowseClick}
        className={cn(
          'flex items-center gap-3 px-3 py-2.5 rounded-lg border-2 border-dashed cursor-pointer transition-all',
          'hover:border-primary hover:bg-primary/5',
          isDragging && 'border-primary bg-primary/10',
          error ? 'border-destructive' : 'border-border'
        )}
      >
        {isUploading ? (
          <>
            <Loader2 className="h-4 w-4 text-primary animate-spin shrink-0" />
            <span className="text-sm text-muted-foreground">Uploading...</span>
          </>
        ) : (
          <>
            <div className={cn('h-8 w-8 shrink-0 rounded-md flex items-center justify-center transition-colors', isDragging ? 'bg-primary/20' : 'bg-neutral-100')}>
              {isDragging
                ? <Upload className="h-4 w-4 text-primary" />
                : <ImageIcon className="h-4 w-4 text-muted-foreground" />
              }
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">
                {isDragging ? 'Drop image here' : 'Click to upload or drag and drop'}
              </p>
              <p className="text-xs text-muted-foreground">{description}</p>
              {error && <p className="text-xs text-destructive font-medium mt-0.5">{error}</p>}
            </div>
          </>
        )}
        <input ref={fileInputRef} type="file" accept={ACCEPTED_TYPES.join(',')} onChange={handleFileInputChange} className="hidden" />
      </div>
    </div>
  )
}
